import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { auth, db } from "./firebase";
import { useAdmin } from "./hooks/useAdmin";
import Sidebar from "./Sidebar";
import "./styles.css";
import { logVisit } from "./utils/visitorTracking"; // 📊 Visitor tracking

import HomePage from "./HomePage"; // 🏠 New Homepage component
import StarRanking from "./StarRanking"; // ⭐ Star Ranking component (previously Home)
import DancerProfile from "./DancerProfile";
import DancerSearch from "./DancerSearch"; // 🔍 Dancer Search component
import SimpleDancerSearch from "./SimpleDancerSearch"; // 🔍 Simple Dancer Search for debugging
import StudioSearch from "./StudioSearch"; // 🔍 Studio Search component
import StudioProfile from "./StudioProfile"; // 🏛️ Studio Profile component
import CompetitionDetails from "./CompetitionDetails"; // ✅ Competition Details component
import Login from "./Login";
import PushToaster from "./components/PushToaster"; // 🔔 in-app toasts for push messages received while the app is open
import InstallPrompt from "./components/InstallPrompt"; // 📲 Install-app + enable-notifications banner (all devices)
import InstallPage from "./InstallPage"; // 📲 Full install guide for every device/browser
import AppInstallTracker from "./components/AppInstallTracker"; // 📲 Records which users run the installed app (admin stats)
import MyFeed from "./MyFeed"; // 📰 Followed dancers/studios feed
import MyDashboard from "./MyDashboard"; // 👤 Verified dancer dashboard
import NotificationSettings from "./NotificationSettings"; // 🔔 Push/email preferences
import UploadCompetition from "./UploadCompetition";
import UploadFlymark from "./UploadFlymark";
import UploadFlymarkNew from "./UploadFlymarkNew"; // 🪰 Flymark New uploader (with judge marks)
import UploadConstasteNew from "./UploadConstasteNew"; // 🎭 Contaste New uploader
import UploadHistory from "./UploadHistory";
import AdminCompetitionViewer from "./AdminCompetitionViewer"; // ✅ Firestore Debug Viewer
import MergeDancers from "./MergeDancers"; // ✅ Merge Dancers Tool
import MergeStudios from "./MergeStudios"; // 🏢 Merge Studios Tool
import StudioTransfers from "./StudioTransfers"; // 🔄 Studio Transfers Tool
import EternalCupWinners from "./EternalCupWinners"; // 🏆 Eternal Cup Winners
import EternalCupRanking from "./EternalCupRanking"; // 🏆 Eternal Cup Ranking
import EternalCupEvent from "./EternalCupEvent"; // 🏆 Eternal Cup Event Page
import EternalCupEventManager from "./EternalCupEventManager"; // 🏆 Eternal Cup Event Manager (Admin)
import StudioRankings from "./StudioRankings"; // 🏛️ Studio Rankings
import CompetitionArchive from "./CompetitionArchive"; // 📁 Competition Archive
import AdminClaimReview from "./AdminClaimReview"; // 👥 Admin Claim Review
import AdminStudioClaimReview from "./AdminStudioClaimReview"; // 🏢 Admin Studio Claim Review
import AdminCacheManager from "./AdminCacheManager"; // 🏎️ Performance Cache Manager
import AdminCachePanel from "./components/AdminCachePanel"; // 🚀 Global Cache Calculator
import DuplicateManager from "./components/DuplicateManager"; // 🔄 Duplicate Competition Manager
import BestDaysVisibilityManager from "./components/BestDaysVisibilityManager"; // 📊 Best Days Visibility Manager
import SponsorshipManager from "./SponsorshipManager"; // 👟 Prima Sponsorship Manager
import CompareDancers from "./CompareDancers"; // 🆚 Compare Dancers Tool
import AdminUsers from "./AdminUsers"; // 👥 Registered Users
import HomepageManager from "./HomepageManager"; // 🏠 Homepage Manager
import GroupRanking from "./GroupRanking"; // 🏆 Group Ranking (Latin, Contemporary, Hip Hop)
import AdminDashboard from "./AdminDashboard"; // 🛠️ Admin Dashboard
import VisitorStatistics from "./VisitorStatistics"; // 📊 Visitor Statistics
import AdminProfileVisits from "./AdminProfileVisits"; // 📊 Profile Visit Statistics
import AdminCompetitionStats from "./AdminCompetitionStats"; // 📊 Competition Statistics
import AdminNotificationSettings from "./AdminNotificationSettings"; // 🔔 Push Notifications
import Marketplace from "./Marketplace"; // 🛒 Dance Marketplace
import AdminMarketplaceReview from "./AdminMarketplaceReview"; // 🛒 Marketplace Admin Review
import AdminStars from "./AdminStars"; // ⭐ Current Stars viewer
import AdminJudges from "./AdminJudges"; // 👨‍⚖️ Judges Directory
import PointsCalculator from "./PointsCalculator"; // 🧮 Points Calculator
import SunoMusicEditor from "./SunoMusicEditor"; // 🎵 Suno Music Editor
import AdminRulesPage from "./AdminRulesPage"; // 📋 Rules & Regulations Manager
import { checkCacheStatus } from "./utils/rankingCalculations";
import { initViewportHeight } from "./utils/viewportUtils"; // 📱 Mobile viewport height fix
// import ViewportDebugInfo from "./components/ViewportDebugInfo"; // 🐛 Uncomment for viewport debugging

// Firestore test component (removed to reduce reads)
function FirestoreTest() {
  useEffect(() => {
    console.log("Firestore test component - using cache instead of direct queries");
  }, []);

  return null;
}

// Visitor tracking component
function VisitorTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    logVisit(location.pathname);
  }, [location]);

  return null;
}

// Admin route protection component
function AdminRoute({ children }) {
  const { isAdmin, loading } = useAdmin();
  
  if (loading) {
    return <div style={{ padding: '2rem' }}>Loading...</div>;
  }
  
  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function App() {
  const [userEmail, setUserEmail] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserEmail(user ? user.email : null);
    });
    return () => unsubscribe();
  }, []);

  // Initialize cache on app startup (if needed)
  useEffect(() => {
    console.log('🚀 App started - cache managed by scheduled functions');
    
    // Initialize mobile viewport height handling
    initViewportHeight();
    
    // Only check cache status for display, don't trigger refresh
    checkCacheStatus()
      .then((cacheStatus) => {
        if (cacheStatus.isStale) {
          console.log(`⚠️ Cache is ${cacheStatus.hoursOld} hours old - needs update`);
          console.log('ℹ️ Cache will be refreshed by scheduled function at 3 AM');
        } else {
          console.log('✅ Cache is up to date');
        }
      })
      .catch((error) => {
        console.error('❌ Error checking cache status:', error);
      });
    
  }, []); // Run only once on app startup

  return (
    <Router>
      <VisitorTracker />
      <PushToaster />
      <InstallPrompt />
      <AppInstallTracker />
      <div className="app-container">
        <Sidebar />

        <main className="main-content">
          <FirestoreTest />
          {/* Uncomment the following line to enable viewport debugging on mobile devices */}
          {/* <ViewportDebugInfo show={true} /> */}
          {userEmail && (
            <div className="user-info">
              <p>
                <strong>Logged in as:</strong> {userEmail}
              </p>
            </div>
          )}

          <Routes>
            {/* 🏠 Homepage - Separate from Star Ranking */}
            <Route path="/" element={<HomePage />} />
            
            {/* ⭐ Star Ranking - Now on its own page */}
            <Route path="/ranking" element={<StarRanking />} />
            
            {/* 👤 Dancer & Studio Routes */}
            <Route path="/dancer/:name" element={<DancerProfile />} />
            <Route path="/studio/:studioName" element={<StudioProfile />} />
            <Route path="/competition/:competitionId" element={<CompetitionDetails />} />
            
            {/* 🏆 Competition & Rankings */}
            <Route path="/eternal-cup" element={<EternalCupEvent />} />
            <Route path="/eternal-cup-winners" element={<EternalCupWinners />} />
            <Route path="/eternal-cup-ranking" element={<EternalCupRanking />} />
            <Route path="/studio-rankings" element={<StudioRankings />} />
            <Route path="/group-ranking" element={<GroupRanking />} />
            <Route path="/compare-dancers" element={<CompareDancers />} />
            <Route path="/points-calculator" element={<PointsCalculator />} />
            
            {/* 📰 Personalisation */}
            <Route path="/my-feed" element={<MyFeed />} />
            <Route path="/my-dashboard" element={<MyDashboard />} />
            <Route path="/notifications" element={<NotificationSettings />} />
            <Route path="/install" element={<InstallPage />} />

            {/* 🔑 Auth */}
            <Route path="/login" element={<Login />} />
            
            {/* Admin-only routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/competition-archive" element={
              <AdminRoute>
                <CompetitionArchive />
              </AdminRoute>
            } />
            <Route path="/homepage-manager" element={
              <AdminRoute>
                <HomepageManager />
              </AdminRoute>
            } />
            <Route path="/eternal-cup-event-manager" element={
              <AdminRoute>
                <EternalCupEventManager />
              </AdminRoute>
            } />
            <Route path="/admin/music" element={
              <AdminRoute>
                <SunoMusicEditor />
              </AdminRoute>
            } />
            <Route path="/upload" element={
              <AdminRoute>
                <UploadCompetition />
              </AdminRoute>
            } />
            <Route path="/upload-flymark" element={
              <AdminRoute>
                <UploadFlymark />
              </AdminRoute>
            } />
            <Route path="/upload-flymark-new" element={
              <AdminRoute>
                <UploadFlymarkNew />
              </AdminRoute>
            } />
            <Route path="/upload-contaste-new" element={
              <AdminRoute>
                <UploadConstasteNew />
              </AdminRoute>
            } />
            <Route path="/history" element={
              <AdminRoute>
                <UploadHistory />
              </AdminRoute>
            } />
            <Route path="/merge-dancers" element={
              <AdminRoute>
                <MergeDancers />
              </AdminRoute>
            } />
            <Route path="/merge-studios" element={
              <AdminRoute>
                <MergeStudios />
              </AdminRoute>
            } />
            <Route path="/studio-transfers" element={
              <AdminRoute>
                <StudioTransfers />
              </AdminRoute>
            } />
            <Route path="/admin-debug" element={
              <AdminRoute>
                <AdminCompetitionViewer />
              </AdminRoute>
            } />
            <Route path="/admin/claims" element={
              <AdminRoute>
                <AdminClaimReview />
              </AdminRoute>
            } />
            <Route path="/admin/studio-claims" element={
              <AdminRoute>
                <AdminStudioClaimReview />
              </AdminRoute>
            } />
            <Route path="/admin/cache" element={
              <AdminRoute>
                <AdminCacheManager />
              </AdminRoute>
            } />
            <Route path="/admin/global-cache" element={
              <AdminRoute>
                <AdminCachePanel />
              </AdminRoute>
            } />
            <Route path="/admin/duplicates" element={
              <AdminRoute>
                <DuplicateManager />
              </AdminRoute>
            } />
            <Route path="/admin/sponsorships" element={
              <AdminRoute>
                <SponsorshipManager />
              </AdminRoute>
            } />
            <Route path="/admin/best-days-visibility" element={
              <AdminRoute>
                <BestDaysVisibilityManager />
              </AdminRoute>
            } />
            <Route path="/admin/visitor-statistics" element={
              <AdminRoute>
                <VisitorStatistics />
              </AdminRoute>
            } />
            <Route path="/admin/competition-stats" element={
              <AdminRoute>
                <AdminCompetitionStats />
              </AdminRoute>
            } />
            <Route path="/admin/stars" element={
              <AdminRoute>
                <AdminStars />
              </AdminRoute>
            } />
            <Route path="/admin/judges" element={
              <AdminRoute>
                <AdminJudges />
              </AdminRoute>
            } />
            <Route path="/admin/profile-visits" element={
              <AdminRoute>
                <AdminProfileVisits />
              </AdminRoute>
            } />
            <Route path="/admin/notifications" element={
              <AdminRoute>
                <AdminNotificationSettings />
              </AdminRoute>
            } />
            <Route path="/marketplace" element={
              <AdminRoute>
                <Marketplace />
              </AdminRoute>
            } />
            <Route path="/admin/marketplace" element={
              <AdminRoute>
                <AdminMarketplaceReview />
              </AdminRoute>
            } />
            
            <Route path="/admin/users" element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            } />
            
            <Route path="/admin/rules" element={
              <AdminRoute>
                <AdminRulesPage />
              </AdminRoute>
            } />

            {/* Search routes at the bottom */}
            <Route path="/search" element={<SimpleDancerSearch />} />
            <Route path="/studio-search" element={<StudioSearch />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}
export default App;